import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Cache a *minimal* user snapshot (safe display fields only — no email or
// contact info) so the app can render the sidebar avatar + plan badge before
// /auth/me returns. The session cookie is httpOnly and never touches
// localStorage; XSS on this cache would only expose a name/picture that is
// already public on the user's showcase page. Full profile is re-fetched
// server-side on every load.
const CACHE_KEY = "pb_auth_cache_v1";
const CACHE_FIELDS = ["user_id", "display_name", "name", "picture", "plan", "role"];

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Expire after 24h so a stale user snapshot never lingers indefinitely.
    if (Date.now() - (parsed.ts || 0) > 24 * 3600 * 1000) return null;
    return parsed.user || null;
  } catch { return null; }
};
const writeCache = (user) => {
  try {
    if (user) {
      const safe = {};
      for (const k of CACHE_FIELDS) if (user[k] !== undefined) safe[k] = user[k];
      localStorage.setItem(CACHE_KEY, JSON.stringify({ user: safe, ts: Date.now() }));
    } else localStorage.removeItem(CACHE_KEY);
  } catch { /* quota / private-mode — swallow */ }
};

export function AuthProvider({ children }) {
  const cached = readCache();
  const [user, setUserRaw] = useState(cached);
  // If we have a cached user, unblock rendering immediately.
  const [loading, setLoading] = useState(!cached);

  const setUser = useCallback((next) => {
    setUserRaw(next);
    writeCache(next);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUserRaw(r.data);
      writeCache(r.data);
    } catch (e) {
      // Only clear the session on an explicit 401 from the server. Network
      // blips, 5xx errors and request timeouts must never log a user out —
      // that was the frequent-logout bug users reported. Keep the cached
      // user in place so the app keeps working while connectivity recovers.
      if (e?.response?.status === 401) {
        setUserRaw(null);
        writeCache(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    await api.post("/auth/logout");
    setUserRaw(null);
    writeCache(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
